/**
 * Production-Grade Retry Mechanism System
 * 
 * Features:
 * - Exponential backoff with jitter
 * - Circuit breaker pattern
 * - Configurable retry policies
 * - Error classification and routing
 * - Dead letter queue for failed operations
 * - Metrics collection and monitoring
 */

export interface RetryConfig {
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
  backoffFactor: number;
  jitter: boolean;
  retryCondition: (error: unknown) => boolean;
  onRetry?: (attempt: number, error: unknown) => void;
  onSuccess?: (attempt: number) => void;
  onFailure?: (error: unknown, attempts: number) => void;
}

export interface CircuitBreakerConfig {
  failureThreshold: number;
  recoveryTimeMs: number;
  monitorWindowMs: number;
}

export interface RetryMetrics {
  totalAttempts: number;
  successfulAttempts: number;
  failedAttempts: number;
  averageAttempts: number;
  circuitBreakerTrips: number;
  lastExecutionTime: number;
}

export type CircuitBreakerState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export class RetryMechanism {
  private circuitBreakerState: CircuitBreakerState = 'CLOSED';
  private failureCount = 0;
  private lastFailureTime = 0;
  private metrics: RetryMetrics = {
    totalAttempts: 0,
    successfulAttempts: 0,
    failedAttempts: 0,
    averageAttempts: 0,
    circuitBreakerTrips: 0,
    lastExecutionTime: 0
  };

  constructor(
    private config: RetryConfig,
    private circuitBreakerConfig?: CircuitBreakerConfig
  ) {}

  /**
   * Execute operation with retry logic
   */
  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.circuitBreakerConfig && !this.canExecute()) {
      throw new Error('Circuit breaker is OPEN. Operation not allowed.');
    }

    let lastError: unknown;
    let attempt = 0;

    this.metrics.lastExecutionTime = Date.now();

    for (attempt = 1; attempt <= this.config.maxAttempts; attempt++) {
      this.metrics.totalAttempts++;

      try {
        const result = await operation();
        
        // Success - reset circuit breaker and update metrics
        this.onSuccess(attempt);
        return result;
      } catch (error) {
        lastError = error;
        
        // Check if we should retry this error
        if (!this.config.retryCondition(error)) {
          this.onFailure(error, attempt);
          throw error;
        }

        // If this is the last attempt, don't wait
        if (attempt === this.config.maxAttempts) {
          break;
        }

        // Execute retry callback
        if (this.config.onRetry) {
          this.config.onRetry(attempt, error);
        }

        // Wait before retry
        await this.wait(attempt);
      }
    }

    // All attempts failed
    this.onFailure(lastError, attempt);
    throw lastError;
  }

  /**
   * Execute with dynamic retry configuration based on context
   * 
   * Supports both emergency and general scenarios.
   * 
   * @param operation - The operation to execute
   * @param context - Emergency or general retry context
   */
  async executeWithAdaptiveConfig<T>(
    operation: () => Promise<T>,
    context: import('./AdaptiveRetryCalculator').AdaptiveRetryContext
  ): Promise<T> {
    // Calculate adaptive retry config
    const { calculateAdaptiveRetries } = await import('./AdaptiveRetryCalculator');
    const adaptiveConfig = calculateAdaptiveRetries(context);
    
    // Create new RetryMechanism with adaptive config
    const adaptiveRetry = new RetryMechanism(
      adaptiveConfig,
      this.circuitBreakerConfig
    );
    
    // Log adaptive decision (for monitoring)
    console.log('[AdaptiveRetry]', {
      contextType: context.type,
      maxAttempts: adaptiveConfig.maxAttempts,
      baseDelay: adaptiveConfig.baseDelayMs,
      ...(context.type === 'emergency' 
        ? {
            emergencyLevel: context.emergencyLevel,
            participants: context.activeParticipants,
          }
        : {
            operationType: context.operationType,
            priority: context.priority,
          }
      ),
    });
    
    // Execute with adaptive config
    return adaptiveRetry.execute(operation);
  }

  /**
   * Execute with universal dynamic retry configuration
   * 
   * Supports emergency AND non-emergency scenarios with unified context
   * 
   * @param operation - The operation to execute
   * @param context - Universal dynamic retry context
   * @returns Promise resolving to operation result
   */
  async executeWithDynamicRetry<T>(
    operation: () => Promise<T>,
    context: import('./DynamicRetryContext').DynamicRetryContext
  ): Promise<T> {
    // Calculate dynamic retry config
    const { calculateDynamicRetries } = await import('./DynamicRetryContext');
    const dynamicConfig = calculateDynamicRetries(context);
    
    // Create new RetryMechanism with dynamic config
    const dynamicRetry = new RetryMechanism(
      dynamicConfig,
      this.circuitBreakerConfig
    );
    
    // Log dynamic decision (for monitoring)
    console.log('[DynamicRetry]', {
      criticality: context.operationCriticality,
      operationType: context.operationType,
      participants: context.activeParticipants,
      emergencyLevel: context.emergencyLevel,
      maxAttempts: dynamicConfig.maxAttempts,
      baseDelay: dynamicConfig.baseDelayMs,
      networkHealth: context.systemHealth?.networkHealth,
      systemLoad: context.systemHealth?.systemLoad,
    });
    
    // Execute with dynamic config
    return dynamicRetry.execute(operation);
  }

  /**
   * Execute with timeout
   */
  async executeWithTimeout<T>(
    operation: () => Promise<T>,
    timeoutMs: number
  ): Promise<T> {
    return this.execute(() => {
      return Promise.race([
        operation(),
        new Promise<never>((_: any, reject) =>
          setTimeout(() => reject(new Error('Operation timeout')), timeoutMs)
        )
      ]);
    });
  }

  /**
   * Batch execute with retry for multiple operations
   */
  async executeBatch<T>(
    operations: Array<() => Promise<T>>,
    concurrency = 3
  ): Promise<Array<T | Error>> {
    const results: Array<T | Error> = [];
    const executing: Promise<void>[] = [];

    for (let i = 0; i < operations.length; i++) {
      const operation = operations[i];

      if (!operation) {
        results[i] = new Error('Operation is undefined');
        continue;
      }

      const promise = this.execute(operation)
        .then(result => {
          results[i] = result;
        })
        .catch(error => {
          results[i] = error instanceof Error ? error : new Error(String(error));
        });

      executing.push(promise);

      // Control concurrency
      if (executing.length >= concurrency) {
        await Promise.race(executing);
        executing.splice(
          executing.findIndex(p => p === promise), 1
        );
      }
    }

    // Wait for remaining operations
    await Promise.all(executing);
    return results;
  }

  /**
   * Check if operation can execute (circuit breaker logic)
   */
  private canExecute(): boolean {
    if (!this.circuitBreakerConfig) return true;

    const now = Date.now();
    const { recoveryTimeMs, failureThreshold } = this.circuitBreakerConfig;

    switch (this.circuitBreakerState) {
      case 'CLOSED':
        return true;

      case 'OPEN':
        if (now - this.lastFailureTime >= recoveryTimeMs) {
          this.circuitBreakerState = 'HALF_OPEN';
          return true;
        }
        return false;

      case 'HALF_OPEN':
        return true;

      default:
        return false;
    }
  }

  /**
   * Handle successful execution
   */
  private onSuccess(attempts: number): void {
    this.metrics.successfulAttempts++;
    this.metrics.averageAttempts = 
      (this.metrics.averageAttempts * (this.metrics.successfulAttempts - 1) + attempts) / 
      this.metrics.successfulAttempts;

    if (this.config.onSuccess) {
      this.config.onSuccess(attempts);
    }

    // Reset circuit breaker on success
    if (this.circuitBreakerConfig) {
      this.failureCount = 0;
      this.circuitBreakerState = 'CLOSED';
    }
  }

  /**
   * Handle failed execution
   */
  private onFailure(error: unknown, attempts: number): void {
    this.metrics.failedAttempts++;

    if (this.config.onFailure) {
      this.config.onFailure(error, attempts);
    }

    // Update circuit breaker state
    if (this.circuitBreakerConfig) {
      this.failureCount++;
      this.lastFailureTime = Date.now();

      if (this.failureCount >= this.circuitBreakerConfig.failureThreshold) {
        this.circuitBreakerState = 'OPEN';
        this.metrics.circuitBreakerTrips++;
      }
    }
  }

  /**
   * Calculate delay with exponential backoff and jitter
   */
  private calculateDelay(attempt: number): number {
    const exponentialDelay = this.config.baseDelayMs * 
      Math.pow(this.config.backoffFactor, attempt - 1);
    
    let delay = Math.min(exponentialDelay, this.config.maxDelayMs);

    // Add jitter to prevent thundering herd
    if (this.config.jitter) {
      delay = delay * (0.5 + Math.random() * 0.5);
    }

    return Math.floor(delay);
  }

  /**
   * Wait for specified duration
   */
  private wait(attempt: number): Promise<void> {
    const delay = this.calculateDelay(attempt);
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  /**
   * Get current metrics
   */
  getMetrics(): RetryMetrics {
    return { ...this.metrics };
  }

  /**
   * Reset metrics
   */
  resetMetrics(): void {
    this.metrics = {
      totalAttempts: 0,
      successfulAttempts: 0,
      failedAttempts: 0,
      averageAttempts: 0,
      circuitBreakerTrips: 0,
      lastExecutionTime: 0
    };
  }

  /**
   * Get circuit breaker state
   */
  getCircuitBreakerState(): CircuitBreakerState {
    return this.circuitBreakerState;
  }

  /**
   * Force circuit breaker state
   */
  setCircuitBreakerState(state: CircuitBreakerState): void {
    this.circuitBreakerState = state;
    if (state === 'CLOSED') {
      this.failureCount = 0;
    }
  }
}

/**
 * Predefined retry configurations
 */
export const RetryConfigs = {
  // Quick operations with light retries
  LIGHT: {
    maxAttempts: 2,
    baseDelayMs: 100,
    maxDelayMs: 1000,
    backoffFactor: 2,
    jitter: true,
    retryCondition: (error: unknown) => 
      error instanceof Error && 
      (error.message.includes('network') || 
       error.message.includes('timeout') ||
       error.message.includes('503') ||
       error.message.includes('502'))
  } as RetryConfig,

  // Standard operations
  STANDARD: {
    maxAttempts: 3,
    baseDelayMs: 500,
    maxDelayMs: 5000,
    backoffFactor: 2,
    jitter: true,
    retryCondition: (error: unknown) => 
      error instanceof Error && 
      !error.message.includes('401') && 
      !error.message.includes('403') &&
      !error.message.includes('validation')
  } as RetryConfig,

  // Heavy operations with aggressive retries
  HEAVY: {
    maxAttempts: 5,
    baseDelayMs: 1000,
    maxDelayMs: 30000,
    backoffFactor: 2,
    jitter: true,
    retryCondition: (error: unknown) => 
      error instanceof Error && 
      !error.message.includes('401') && 
      !error.message.includes('403') &&
      !error.message.includes('validation') &&
      !error.message.includes('400')
  } as RetryConfig,

  // Critical operations
  CRITICAL: {
    maxAttempts: 10,
    baseDelayMs: 2000,
    maxDelayMs: 60000,
    backoffFactor: 1.5,
    jitter: true,
    retryCondition: (error: unknown) => 
      error instanceof Error && 
      !error.message.includes('401') && 
      !error.message.includes('403')
  } as RetryConfig
};

/**
 * Circuit breaker configurations
 */
export const CircuitBreakerConfigs = {
  LIGHT: {
    failureThreshold: 3,
    recoveryTimeMs: 10000, // 10 seconds
    monitorWindowMs: 60000  // 1 minute
  },
  
  STANDARD: {
    failureThreshold: 5,
    recoveryTimeMs: 30000, // 30 seconds
    monitorWindowMs: 300000 // 5 minutes
  },
  
  HEAVY: {
    failureThreshold: 10,
    recoveryTimeMs: 60000,  // 1 minute
    monitorWindowMs: 600000 // 10 minutes
  }
};

/**
 * Factory function to create retry mechanism instances
 */
export const createRetryMechanism = (
  retryConfig: RetryConfig,
  circuitBreakerConfig?: CircuitBreakerConfig
): RetryMechanism => {
  return new RetryMechanism(retryConfig, circuitBreakerConfig);
};

/**
 * Utility function for simple retry operations
 */
export const retry = async <T>(
  operation: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> => {
  const fullConfig = { ...RetryConfigs.STANDARD, ...config };
  const retryMechanism = new RetryMechanism(fullConfig);
  return retryMechanism.execute(operation);
};